import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export function useJobs() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getJobsByClient = useCallback(async (clientId) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('client_id', clientId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getJobById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getJobWithRecords = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: job, error: jobError } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .maybeSingle();
      if (jobError) throw jobError;
      if (!job) throw new Error('Job not found');

      const [quotesRes, invoicesRes, expensesRes] = await Promise.all([
        supabase
          .from('quotes')
          .select('id, project_name, job_description, total, status, created_at, client_name')
          .eq('job_id', id)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('invoices')
          .select('id, client_name, total, status, due_date, created_at')
          .eq('job_id', id)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('expenses')
          .select('id, description, amount, category, expense_date')
          .eq('job_id', id)
          .eq('user_id', user.id)
          .order('expense_date', { ascending: false }),
      ]);

      return {
        job,
        quotes: quotesRes.data || [],
        invoices: invoicesRes.data || [],
        expenses: expensesRes.data || [],
      };
    } catch (err) {
      setError(err.message);
      return { job: null, quotes: [], invoices: [], expenses: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  const createJob = useCallback(async (jobData) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('jobs')
        .insert({ ...jobData, created_at: new Date().toISOString(), updated_at: new Date().toISOString() })
        .select()
        .single();
      if (error) throw error;
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateJob = useCallback(async (id, updates) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('jobs')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteJob = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('jobs')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);
      if (error) throw error;
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, getJobsByClient, getJobById, getJobWithRecords, createJob, updateJob, deleteJob };
}
